import type { HexCoord, HexGrid, HexCell } from "../types.ts";

export function coordToKey(coord: HexCoord): string {
  return `${coord.q},${coord.r}`;
}

export function keyToCoord(key: string): HexCoord {
  const [q, r] = key.split(",").map(Number);
  return { q: q!, r: r! };
}

// Flat-top hex neighbors: E, NE, NW, W, SW, SE
const NEIGHBOR_OFFSETS: [number, number][] = [
  [+1, 0],
  [+1, -1],
  [0, -1],
  [-1, 0],
  [-1, +1],
  [0, +1],
];

export function getNeighbors(coord: HexCoord): HexCoord[] {
  return NEIGHBOR_OFFSETS.map(([dq, dr]) => ({
    q: coord.q + dq!,
    r: coord.r + dr!,
  }));
}

export function hexDistance(a: HexCoord, b: HexCoord): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

export function hexRange(radius: number): HexCoord[] {
  const coords: HexCoord[] = [];
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      coords.push({ q, r });
    }
  }
  return coords;
}

// Flat-top hex: axial → pixel
export function hexToPixel(
  coord: HexCoord,
  size: number,
  cx: number,
  cy: number
): { x: number; y: number } {
  const x = cx + size * (3 / 2) * coord.q;
  const y = cy + size * (Math.sqrt(3) / 2 * coord.q + Math.sqrt(3) * coord.r);
  return { x, y };
}

// Pixel → nearest hex (flat-top)
export function pixelToHex(
  px: number,
  py: number,
  size: number,
  cx: number,
  cy: number
): HexCoord {
  const x = (px - cx) / size;
  const y = (py - cy) / size;

  const q = (2 / 3) * x;
  const r = (-1 / 3) * x + (Math.sqrt(3) / 3) * y;

  return hexRound(q, r);
}

function hexRound(q: number, r: number): HexCoord {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  let rs = Math.round(s);

  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);

  if (dq > dr && dq > ds) {
    rq = -rr - rs;
  } else if (dr > ds) {
    rr = -rq - rs;
  }

  return { q: rq, r: rr };
}

export function serializeHexGrid(grid: HexGrid): { radius: number; cells: Record<string, HexCell>; targetColor: string | null } {
  return {
    radius: grid.radius,
    cells: Object.fromEntries(grid.cells),
    targetColor: grid.targetColor,
  };
}

export function deserializeHexGrid(data: { radius: number; cells: Record<string, HexCell>; targetColor: string | null }): HexGrid {
  return {
    radius: data.radius,
    cells: new Map(Object.entries(data.cells)),
    targetColor: data.targetColor,
  };
}
